import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  IconButton, 
  Checkbox, 
  InputAdornment, 
  Chip, 
  MenuItem, 
  Select, 
  FormControl,
  InputLabel, 
  Tooltip,
  CircularProgress,
  Popover,
  Stack
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningIcon from '@mui/icons-material/Warning';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import TodayIcon from '@mui/icons-material/Today';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ProjectCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    assignee: 'all',
    type: 'all',
    status: 'all'
  });
  const {projectId} = useParams();

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  // Month/Year picker state
  const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
  const [tempYear, setTempYear] = useState(currentYear);
  const [tempMonth, setTempMonth] = useState(currentMonth);

  // Fetch sprints for the current month view
  const fetchSprintsForMonth = async () => {
    setLoading(true);
    try {
      const firstDay = new Date(currentYear, currentMonth, 1);
      const lastDay = new Date(currentYear, currentMonth + 1, 0);
      
      const extendedStart = new Date(firstDay);
      extendedStart.setDate(firstDay.getDate() - 14);
      const extendedEnd = new Date(lastDay);
      extendedEnd.setDate(lastDay.getDate() + 14);
      
      const startDate = extendedStart.toISOString().split('T')[0];
      const endDate = extendedEnd.toISOString().split('T')[0];
      
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await fetch(`${API_URL}/api/sprints/calendersprint/calendar?startDate=${startDate}&endDate=${endDate}&projectId=${projectId}`,
       {
         headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
      );
      
      if (response.ok) {
        const sprintData = await response.json();
        
        const processedSprints = sprintData.map(sprint => {
          const processedSprint = {
            id: sprint.id,
            name: sprint.name,
            projectKey: sprint.project?.key || sprint.projectKey || '',
            projectName: sprint.project?.name || '',
            startDate: new Date(sprint.startDate),
            endDate: new Date(sprint.endDate),
            status: sprint.status
          };
          return processedSprint;
        });
        
        setSprints(processedSprints);
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when month changes
  useEffect(() => {
    if (projectId) {
      fetchSprintsForMonth();
    }
  }, [currentYear, currentMonth, projectId]);

  // Month navigation
  const previousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  // Go to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Open month/year picker
  const handlePickerOpen = (event) => {
    setTempYear(currentYear);
    setTempMonth(currentMonth);
    setPickerAnchorEl(event.currentTarget);
  };

  // Close month/year picker
  const handlePickerClose = () => {
    setPickerAnchorEl(null);
  };

  // Apply month/year selection
  const handlePickerApply = () => {
    setCurrentDate(new Date(tempYear, tempMonth, 1));
    handlePickerClose();
  };

  // Generate years for dropdown (current year - 10 to current year + 5)
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 10; year <= currentYear + 5; year++) {
      years.push(year);
    }
    return years;
  };

  // Month names for display
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // FIXED: Day headers starting with Sunday
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Handle drag and drop for tasks
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    const [_, year, month, day] = destination.droppableId.split('-');
    const newDueDate = new Date(parseInt(year), parseInt(month), parseInt(day));
    
    const updatedTasks = tasks.map(task => {
      if (task.id === draggableId) {
        return { ...task, dueDate: newDueDate };
      }
      return task;
    });
    
    setTasks(updatedTasks);
  };

  // Helper function to check if a date falls within a sprint
  const isDateInSprint = (date, sprint) => {
    const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const sprintStart = new Date(sprint.startDate.getFullYear(), sprint.startDate.getMonth(), sprint.startDate.getDate());
    const sprintEnd = new Date(sprint.endDate.getFullYear(), sprint.endDate.getMonth(), sprint.endDate.getDate());
    
    const isInSprint = checkDate >= sprintStart && checkDate <= sprintEnd;
    return isInSprint;
  };

  // useEffect(() => {
  //   if (sprints.length > 0) {
     
  //   }
  // }, [sprints]);

  // FIXED: Generate calendar grid with Sunday as first day
  const generateCalendarGrid = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    
    // FIXED: Get the day of week (0 = Sunday, 6 = Saturday)
    // Sunday is 0, so we keep it as is for Sunday-based calendar
    const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    const daysInMonth = lastDayOfMonth.getDate();
    const prevMonth = new Date(currentYear, currentMonth - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();

    // Create array of all days to display (42 days = 6 rows)
    const totalDays = 42; // 6 weeks
    const calendarDays = [];

    // Calculate starting date of the calendar view (going back to the previous Sunday)
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayOfWeek);

    // Generate all days
    for (let i = 0; i < totalDays; i++) {
      const currentDateObj = new Date(startDate);
      currentDateObj.setDate(startDate.getDate() + i);
      
      const isCurrentMonth = currentDateObj.getMonth() === currentMonth;
      
      calendarDays.push({
        date: currentDateObj,
        day: currentDateObj.getDate(),
        isCurrentMonth: isCurrentMonth,
        isToday: currentDateObj.getDate() === new Date().getDate() && 
                 currentDateObj.getMonth() === new Date().getMonth() && 
                 currentDateObj.getFullYear() === new Date().getFullYear()
      });
    }

    // Split into weeks
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return weeks.map((week, weekIndex) => (
      <Grid container key={`week-${weekIndex}`} sx={{ width: '100%', flexWrap: 'nowrap' }}>
        {week.map((dayObj, dayIndex) => {
          const { day, date, isCurrentMonth, isToday } = dayObj;
          
          // Get day sprints
          const daySprints = sprints.filter(sprint => {
            return isDateInSprint(date, sprint);
          });

          return (
            <Grid 
              item 
              xs 
              key={`day-${date.getTime()}`}
              sx={{ 
                minWidth: 0,
                flexBasis: '14.285%',
                maxWidth: '14.285%'
              }}
            >
              <Paper 
                elevation={0} 
                variant="outlined" 
                sx={{ 
                  height: '150px', 
                  position: 'relative',
                  backgroundColor: isToday ? 'rgba(25, 118, 210, 0.08)' : 'white',
                  opacity: isCurrentMonth ? 1 : 0.3,
                  overflow: 'hidden',
                  borderRadius: 0,
                  borderLeft: dayIndex === 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                  }
                }}
              >
                <Box p={0.5}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      display: 'inline-block',
                      fontWeight: isToday ? 'bold' : 'normal',
                      color: isCurrentMonth ? 'inherit' : 'text.secondary',
                      borderRadius: 1,
                      px: 0.5,
                      backgroundColor: isToday ? 'primary.main' : 'transparent',
                      color: isToday ? 'white' : 'inherit',
                      fontSize: '0.75rem'
                    }}
                  >
                    {day}
                  </Typography>
                  
                  {/* Sprints */}
                  {daySprints.length > 0 && (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      mt: 0.5,
                      gap: 0.25
                    }}>
                      {daySprints.slice(0, 3).map((sprint, index) => {
                        return (
                          <Chip
                            key={`${sprint.id}-${date.getTime()}-${index}`}
                            icon={<CalendarMonthIcon sx={{ fontSize: '10px !important' }} />}
                            label={`${sprint.projectKey} - ${sprint.name}`}
                            size="small"
                            sx={{ 
                              backgroundColor: sprint.status === 'ACTIVE' ? '#1976d2' : 
                                             sprint.status === 'COMPLETED' ? '#2e7d32' : '#757575', 
                              color: 'white',
                              fontSize: '0.5rem',
                              height: '18px',
                              width: '100%',
                              justifyContent: 'flex-start',
                              '& .MuiChip-label': {
                                paddingLeft: '2px',
                                paddingRight: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.5rem'
                              },
                              '& .MuiChip-icon': {
                                marginLeft: '2px',
                                marginRight: '1px',
                                fontSize: '10px !important'
                              }
                            }}
                          />
                        );
                      })}
                      {daySprints.length > 3 && (
                        <Typography variant="caption" sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>
                          +{daySprints.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    ));
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header with controls */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={goToToday}>
              Today
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: 1 }}>
              <IconButton size="small" onClick={previousMonth} disabled={loading}>
                <ChevronLeftIcon sx={{ fontSize: 16 }} />
              </IconButton>
              
              {/* Clickable month/year display */}
              <Button 
                onClick={handlePickerOpen}
                disabled={loading}
                sx={{ 
                  px: 2, 
                  py: 0.5, 
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textTransform: 'none',
                  fontWeight: 'medium'
                }}
              >
                <Typography variant="body2">
                  {monthNames[currentMonth]} {currentYear}
                </Typography>
                <ArrowDropDownIcon fontSize="small" />
                {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Button>
              
              <IconButton size="small" onClick={nextMonth} disabled={loading}>
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Month/Year Picker Popover */}
        <Popover
          open={Boolean(pickerAnchorEl)}
          anchorEl={pickerAnchorEl}
          onClose={handlePickerClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 3, minWidth: 300 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Select Month & Year
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Month</InputLabel>
                  <Select
                    value={tempMonth}
                    onChange={(e) => setTempMonth(e.target.value)}
                    label="Month"
                  >
                    {monthNames.map((month, index) => (
                      <MenuItem key={index} value={index}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={tempYear}
                    onChange={(e) => setTempYear(e.target.value)}
                    label="Year"
                  >
                    {generateYears().map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={handlePickerClose}>Cancel</Button>
              <Button variant="contained" onClick={handlePickerApply}>
                Apply
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Calendar */}
        <Box sx={{ px: 2 }}>
          {/* Day headers - FIXED: Now shows Sun, Mon, Tue... */}
          <Grid container sx={{ width: '100%', flexWrap: 'nowrap' }}>
            {daysOfWeek.map((day, index) => (
              <Grid 
                item 
                xs 
                key={day}
                sx={{ 
                  flexBasis: '14.285%',
                  maxWidth: '14.285%'
                }}
              >
                <Box sx={{ 
                  p: 1, 
                  textAlign: 'center',
                  borderLeft: index === 0 ? '1px solid rgba(0, 0, 0, 0.12)' : 'none',
                  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                  borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {day}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          
          {/* Calendar grid */}
          <Box>
            {generateCalendarGrid()}
          </Box>
        </Box>
      </Box>
    </DragDropContext>
  );
};

export default ProjectCalendar;